import { createFileRoute, type SearchSchemaInput } from "@tanstack/react-router";
import { SucheSeite } from "@/components/SucheSeite";
import {
  alsSearch,
  ausSearch,
  OHNE_LEERE_EINZELWERTE,
  type SucheFilter,
} from "@/lib/suche-filter";

/*
 * `/suche`. Die zweite Route mit Search-Params — und die letzte der vier
 * Datensichten.
 *
 * ═══ Zeichengleich mit `verkaufssuche.tsx`, und das ist der Punkt ════════
 *
 * Beide Routen ziehen dieselbe Grenze über dieselbe Funktion. Das ist keine
 * Verdopplung aus Bequemlichkeit: `ausSearch` und `alsSearch` stehen EINMAL in
 * `lib/suche-filter.ts`, hier steht nur die Verdrahtung. Zwei eigene
 * Auslegungen derselben URL wären zwei Wahrheiten — und die Links sollen
 * zwischen den beiden Sichten austauschbar sein, bis auf den Pfad.
 *
 * ═══ Was `validateSearch` leistet ════════════════════════════════════════
 *
 * Es ist die Grenze zwischen „unvertraut" und „getypt". Was aus der URL kommt,
 * kann fehlen, doppelt vorkommen oder vom falschen Typ sein — ein Lesezeichen
 * aus dem Altsystem, eine von Hand getippte Adresse, ein alter Link aus einer
 * E-Mail. `ausSearch` macht daraus einen vollständigen `SucheFilter`: jedes der
 * neunzehn Felder ist danach da, unbekannte Parameter sind weg.
 *
 * ═══ Warum `alsSearch` beim Schreiben dazwischensteht ════════════════════
 *
 * Weil der Filter VOLLSTÄNDIG ist und die URL es nicht sein soll. Ein direkt
 * hinausgereichter `SucheFilter` schriebe `?q=&bw=&sort=&page=` an jede
 * Adresse: `stringifySearch` lässt `undefined` weg, aber `""` behält es — dort
 * ist der leere Wert der bedeutungstragende `loc=`-Marker. `alsSearch` trifft
 * die Unterscheidung, die diese Kodierung selbst nicht treffen kann.
 *
 * ═══ Und warum `alsSearch` allein NICHT gereicht hat ═════════════════════
 *
 * Genau das, wogegen `alsSearch` antritt, stand hier zwei Durchgänge lang in
 * der Adresse — aufgerufen `/suche`, danach `/suche?q=&bw=&sort=&page=`, ohne
 * dass jemand einen Filter angefasst hätte. Der Router hebt die Auswahl
 * wieder auf: beim Mount baut er die Adresse mit `_includeValidateSearch`
 * neu, `validateSearch` liefert dort alle neunzehn Felder, und was `alsSearch`
 * weggelassen hatte, ist wieder da. Auf dem Klick-Weg dasselbe.
 *
 * `search.middlewares` ist der Haken, der DANACH greift — die vollständige
 * Begründung samt gemessener Gegenrichtung steht bei
 * `OHNE_LEERE_EINZELWERTE`. Sie ersetzt `alsSearch` nicht: der Rückruf unten
 * wählt weiterhin aus, und Aufrufer ausserhalb einer Route (Drill-Down-Links)
 * haben nur `alsSearch`.
 *
 * ═══ Kein `loader`, kein `beforeLoad` ════════════════════════════════════
 *
 * Wie bei den drei Schwestersichten: die Daten holen die Query-Hooks der
 * Komponenten — die Trefferliste in `SucheSeite`, Facetten und Auswahllisten in
 * `SucheFilterleiste`. Damit gehören Lade-, Fehler- und Zugangszustand
 * derselben Stelle, die sie auch anzeigt, und ein 401 landet nicht als
 * Router-Fehler neben der Seite, sondern als Aussage darin.
 *
 * ═══ Der Navigationspunkt kommt von selbst ═══════════════════════════════
 *
 * `/suche` steht serverseitig längst in `auth.NavItems`; `Navigation.tsx` lässt
 * den Punkt bisher nur weg, weil die Route fehlte (sie verlinkt ausschliesslich
 * Pfade, die im Routenbaum stehen). Mit dieser Datei steht er im Generat, und
 * damit erscheint der Punkt — ohne dass an `Navigation.tsx` etwas zu ändern
 * wäre.
 */
export const Route = createFileRoute("/suche")({
  component: SucheRoute,
  /*
   * `& SearchSchemaInput` ist kein Zierrat, sondern der Schalter: nur mit
   * dieser Markierung im PARAMETERTYP führt TanStack Eingabe und Ausgabe
   * getrennt. Ohne sie leitet es beides aus dem Rückgabetyp ab, und ein
   * `navigate({ search })` müsste alle neunzehn Felder mitschicken.
   *
   * Der Typ selbst trägt zur Laufzeit nichts; `ausSearch` nimmt weiterhin jedes
   * rohe Objekt entgegen und macht daraus einen vollständigen Filter.
   */
  validateSearch: (roh: Record<string, unknown> & SearchSchemaInput): SucheFilter =>
    ausSearch(roh),
  // Läuft NACH `validateSearch` und hat damit das letzte Wort über die
  // Adresse — die Kette wickelt sich von aussen nach innen ab, und diese
  // Stufe steht aussen. Begründung: `lib/suche-filter.ts`.
  search: { middlewares: [OHNE_LEERE_EINZELWERTE] },
});

function SucheRoute() {
  const filter = Route.useSearch();
  const navigate = Route.useNavigate();

  return (
    <SucheSeite
      filter={filter}
      aufFilter={(neu) => {
        /*
         * `void`: `navigate` liefert ein Promise, auf das hier niemand wartet.
         * Das ist gewollt — die Navigation ist der Effekt, nicht ihr Abschluss.
         * Ohne die Markierung meldet `no-floating-promises` zu Recht.
         */
        void navigate({
          search: alsSearch(neu),
          replace: neu.page === "" || neu.page === filter.page,
          resetScroll: false,
        });
      }}
    />
  );
}
